import React from 'react';
import { useAudioRecognition } from '../AudioRecognitionContext';
import type { AudioRecRecognitionHints } from '../types';

interface UseExpectedMidiHintsOptions {
  enabled?: boolean;
  getExpectedMidiNotes: () => number[];
  mode?: AudioRecRecognitionHints['mode'];
  pollMs?: number;
}

const normalizeExpectedMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((a, b) => a - b);

const buildHintSignature = (hints: AudioRecRecognitionHints | null) =>
  hints ? `${hints.mode ?? 'default'}:${(hints.expectedMidiNotes ?? []).join(',')}` : 'null';

export const useExpectedMidiHints = ({
  enabled = true,
  getExpectedMidiNotes,
  mode = 'exercise',
  pollMs = 80,
}: UseExpectedMidiHintsOptions) => {
  const { setRecognitionHints } = useAudioRecognition();
  const getExpectedMidiNotesRef = React.useRef(getExpectedMidiNotes);
  const lastSignatureRef = React.useRef<string | null>(null);
  const didWarnHintReadRef = React.useRef(false);

  getExpectedMidiNotesRef.current = getExpectedMidiNotes;

  React.useEffect(() => {
    if (!enabled) {
      lastSignatureRef.current = null;
      didWarnHintReadRef.current = false;
      setRecognitionHints(null);
      return;
    }

    lastSignatureRef.current = '__init__';

    const pushHints = () => {
      let expectedMidiNotes: number[] = [];

      try {
        expectedMidiNotes = normalizeExpectedMidiNotes(getExpectedMidiNotesRef.current());
        didWarnHintReadRef.current = false;
      } catch (error) {
        if (!didWarnHintReadRef.current) {
          console.warn(
            '[AudioRecognition] Failed to resolve expected MIDI notes for hints.',
            error,
          );
          didWarnHintReadRef.current = true;
        }
      }

      const hints =
        expectedMidiNotes.length > 0
          ? {
              mode,
              expectedMidiNotes,
            }
          : null;
      const signature = buildHintSignature(hints);
      if (signature === lastSignatureRef.current) return;
      lastSignatureRef.current = signature;
      setRecognitionHints(hints);
    };

    pushHints();
    const intervalId = window.setInterval(pushHints, Math.max(40, pollMs));

    return () => {
      window.clearInterval(intervalId);
      lastSignatureRef.current = null;
      didWarnHintReadRef.current = false;
      setRecognitionHints(null);
    };
  }, [enabled, mode, pollMs, setRecognitionHints]);
};
